import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MealPlannerAPI } from '../services/api';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';
import { 
  MealPlan, 
  UserPreferences, 
  MealPlanRequest, 
  AutoSaveStatus,
  AutoSaveBehavior
} from '../types/mealPlanner';
import toast from 'react-hot-toast';

// Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Data context interface
interface DataContextType {
  // Meal Plan operations
  mealPlans: {
    current: MealPlan | null;
    isLoading: boolean;
    error: string | null;
    generate: (request: MealPlanRequest) => Promise<MealPlan | null>;
    save: (weekStartDate: string, mealPlan: MealPlan, preferences?: UserPreferences) => Promise<boolean>;
    saveAnonymous: (weekStartDate: string, mealPlan: MealPlan, preferences?: UserPreferences) => Promise<boolean>;
    load: (weekStartDate: string) => Promise<MealPlan | null>;
    delete: (weekStartDate: string) => Promise<boolean>;
    copy: (sourceWeek: string, targetWeek: string) => Promise<boolean>;
    rerollMeal: (request: any) => Promise<boolean>;
    updateGroceryList: (weekStartDate: string, updates: any) => Promise<boolean>;
    updateSideDishes: (weekStartDate: string, mealId: number, sideDishIds: number[]) => Promise<boolean>;
    sideDishes: {
      reroll: (request: any) => Promise<boolean>;
      add: (request: any) => Promise<boolean>;
      remove: (request: any) => Promise<boolean>;
    };
  };
  
  // User Preferences operations
  preferences: {
    data: UserPreferences | null;
    isLoading: boolean;
    error: string | null;
    save: (preferences: UserPreferences) => Promise<boolean>;
    load: () => Promise<UserPreferences | null>;
    reset: () => Promise<boolean>;
  };
  
  // Auto-save operations
  autoSave: {
    status: AutoSaveStatus | null;
    isLoading: boolean;
    updateBehavior: (behavior: AutoSaveBehavior) => Promise<boolean>;
  };
  
  // Side dish operations
  sideDishes: {
    reroll: (request: any) => Promise<boolean>;
    add: (request: any) => Promise<boolean>;
    remove: (request: any) => Promise<boolean>;
  };
  
  // Utility methods
  invalidateQueries: (queryKey: string[]) => void;
  refetchQueries: (queryKey: string[]) => void;
}

// Create context
export const DataContext = createContext<DataContextType | undefined>(undefined);

// Provider component
interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <DataContextContent>
        {children}
      </DataContextContent>
    </QueryClientProvider>
  );
};

// Main context content
const DataContextContent: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, anonymousSessionId } = useAuth();
  const queryClient = useQueryClient();

  // Get user ID for queries - memoized to prevent unnecessary re-renders
  const getUserId = useMemo(() => {
    if (isAuthenticated && user) return user.id.toString();
    if (anonymousSessionId) return anonymousSessionId;
    return null;
  }, [isAuthenticated, user, anonymousSessionId]);

  // Memoize query keys to prevent unnecessary re-fetches
  const mealPlanQueryKey = useMemo(() => ['mealPlan', 'current', getUserId], [getUserId]);
  const preferencesQueryKey = useMemo(() => ['preferences', getUserId], [getUserId]);
  const autoSaveQueryKey = useMemo(() => ['autoSave', user?.id], [user?.id]);

  // Meal Plan Queries
  const {
    data: currentMealPlan,
    isLoading: mealPlanLoading,
    error: mealPlanError
  } = useQuery({
    queryKey: mealPlanQueryKey,
    queryFn: async () => {
      if (!getUserId) return null;
      
      try {
        if (isAuthenticated && user) {
          const response = await MealPlannerAPI.getCurrentMealPlan();
          return response.success ? response.data?.mealPlan || null : null;
        } else {
          const response = await MealPlannerAPI.getLastAnonymousMealPlan();
          return response.success ? response.data?.mealPlan || null : null;
        }
      } catch (error) {
        console.error('Failed to load current meal plan:', error);
        return null;
      }
    },
    enabled: !!getUserId,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    refetchOnMount: false, // Don't refetch when component mounts if data is fresh
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  // User Preferences Query
  const {
    data: userPreferences,
    isLoading: preferencesLoading,
    error: preferencesError
  } = useQuery({
    queryKey: preferencesQueryKey,
    queryFn: async () => {
      if (!getUserId) return null;
      
      try {
        const response = await MealPlannerAPI.getUserPreferences(getUserId);
        logger.dataContext('Loaded preferences from API:', response);
        const preferences = response.success ? response.data : null;
        logger.dataContext('Final preferences data:', preferences);
        return preferences;
      } catch (error) {
        logger.error('Failed to load user preferences:', error);
        return null;
      }
    },
    enabled: !!getUserId,
  });

  // Auto-save Status Query (authenticated users only)
  const {
    data: autoSaveStatus,
    isLoading: autoSaveLoading
  } = useQuery({
    queryKey: autoSaveQueryKey,
    queryFn: async () => {
      if (!isAuthenticated || !user) return null;
      
      try {
        const response = await MealPlannerAPI.getAutoSaveStatus();
        return response.success ? response.data?.autoSaveStatus : null;
      } catch (error) {
        logger.error('Failed to load auto-save status:', error);
        return null;
      }
    },
    enabled: !!isAuthenticated && !!user,
  });

  // Mutations
  const generateMealPlanMutation = useMutation({
    mutationFn: async (request: MealPlanRequest) => {
      const response = await MealPlannerAPI.generateMealPlan(request);
      if (!response.success) {
        throw new Error(response.error || 'Failed to generate meal plan');
      }
      return response.data?.mealPlan;
    },
          onSuccess: (mealPlan) => {
        // Force a re-render by invalidating the query to ensure fresh data
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        toast.success('Meal plan generated successfully!');
      },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate meal plan');
    },
  });

  const saveMealPlanMutation = useMutation({
    mutationFn: async ({ weekStartDate, mealPlan, preferences }: {
      weekStartDate: string;
      mealPlan: MealPlan;
      preferences?: UserPreferences;
    }) => {
      if (isAuthenticated && user) {
        const response = await MealPlannerAPI.saveMealPlan(weekStartDate, mealPlan, preferences);
        if (!response.success) {
          throw new Error(response.error || 'Failed to save meal plan');
        }
        return response.data;
      } else {
        const response = await MealPlannerAPI.saveAnonymousMealPlan(weekStartDate, mealPlan, preferences);
        if (!response.success) {
          throw new Error(response.error || 'Failed to save anonymous meal plan');
        }
        return response.data;
      }
    },
          onSuccess: () => {
        toast.success('Meal plan saved successfully!');
        // Invalidate and refetch current meal plan
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
      },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save meal plan');
    },
  });

  const savePreferencesMutation = useMutation({
    mutationFn: async (preferences: UserPreferences) => {
      if (!getUserId) throw new Error('No user ID available');
      
      const response = await MealPlannerAPI.saveUserPreferences(getUserId, preferences);
      if (!response.success) {
        throw new Error(response.error || 'Failed to save preferences');
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success('Preferences saved successfully!');
      // Update preferences in cache
      queryClient.invalidateQueries({ queryKey: preferencesQueryKey });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save preferences');
    },
  });

  const updateAutoSaveBehaviorMutation = useMutation({
    mutationFn: async (behavior: AutoSaveBehavior) => {
      if (!isAuthenticated || !user) throw new Error('User not authenticated');
      
      const response = await MealPlannerAPI.updateAutoSaveBehavior(behavior);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update auto-save behavior');
      }
      return response.data;
    },
    onSuccess: (_: any, behavior: AutoSaveBehavior) => {
      toast.success(`Auto-save set to ${behavior}`);
      // Update auto-save status in cache
      queryClient.invalidateQueries({ queryKey: ['autoSave', user?.id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update auto-save behavior');
    },
  });

  const rerollMealMutation = useMutation({
    mutationFn: async (request: any) => {
      const response = await MealPlannerAPI.rerollMeal(request);
      if (!response.success) {
        throw new Error(response.error || 'Failed to reroll meal');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Meal rerolled successfully!');
      
      // Immediately update the cache with the rerolled meal plan data
      if (data && data.updatedMealPlan) {
        // The cache expects the meal plan data directly, not wrapped in an object
        queryClient.setQueryData(mealPlanQueryKey, data.updatedMealPlan);
        console.log('✅ [DATA_CONTEXT] Cache updated with rerolled meal plan, no full refetch needed');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reroll meal');
    },
  });

  const rerollSideDishMutation = useMutation({
    mutationFn: async (request: any) => {
      const response = await MealPlannerAPI.rerollSideDish(request);
      if (!response.success) {
        throw new Error(response.error || 'Failed to reroll side dish');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Side dish rerolled successfully!');

      if (!data) return;

      // If we received a full meal plan, set it directly
      if (data && Array.isArray((data as any)?.days)) {
        queryClient.setQueryData(mealPlanQueryKey, data);
        return;
      }

      // If backend indicates exhaustion wrapper: { mealId, exhausted: true, updatedMeal }
      if ((data as any)?.exhausted && (data as any)?.updatedMeal && (data as any)?.mealId) {
        const { mealId, updatedMeal } = data as any;
        queryClient.setQueryData(mealPlanQueryKey, (prev: any) => {
          if (!prev || !Array.isArray(prev?.days)) return prev;
          return {
            ...prev,
            days: prev.days.map((day: any) => ({
              ...day,
              meals: day.meals.map((meal: any) =>
                meal.id === mealId
                  ? {
                      ...meal,
                      sideDishes: [],
                      ingredients: updatedMeal?.ingredients || meal.ingredients,
                    }
                  : meal
              ),
            })),
          };
        });
        // Ensure derived totals/grocery list reflect latest server state
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        return;
      }

      // If we received a single updated meal, patch it into the current plan
      if ((data as any)?.id) {
        const updatedMeal = data as any;
        queryClient.setQueryData(mealPlanQueryKey, (prev: any) => {
          if (!prev || !Array.isArray(prev?.days)) return prev;
          return {
            ...prev,
            days: prev.days.map((day: any) => ({
              ...day,
              meals: day.meals.map((meal: any) =>
                meal.id === updatedMeal.id
                  ? {
                      ...meal,
                      sideDishes: updatedMeal?.sideDishes ?? meal.sideDishes,
                      ingredients: updatedMeal?.ingredients ?? meal.ingredients,
                    }
                  : meal
              ),
            })),
          };
        });
        // Invalidate to refresh totals/grocery list as needed
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        return;
      }

      // Fallback: refetch if shape is unexpected
      queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reroll side dish');
    },
  });

  // Define all callback functions at the top level
  const generateMealPlan = useCallback(async (request: MealPlanRequest) => {
    try {
      const mealPlan = await generateMealPlanMutation.mutateAsync(request);
      return mealPlan || null;
    } catch (error) {
      return null;
    }
  }, [generateMealPlanMutation]);

  const saveMealPlan = useCallback(async (weekStartDate: string, mealPlan: MealPlan, preferences?: UserPreferences) => {
    try {
      await saveMealPlanMutation.mutateAsync({ weekStartDate, mealPlan, preferences });
      return true;
    } catch (error) {
      return false;
    }
  }, [saveMealPlanMutation]);

  const saveAnonymousMealPlan = useCallback(async (weekStartDate: string, mealPlan: MealPlan, preferences?: UserPreferences) => {
    try {
      await saveMealPlanMutation.mutateAsync({ weekStartDate, mealPlan, preferences });
      return true;
    } catch (error) {
      return false;
    }
  }, [saveMealPlanMutation]);

  const loadMealPlan = useCallback(async (weekStartDate: string) => {
    try {
      const response = await MealPlannerAPI.getMealPlanByWeek(weekStartDate);
      console.log('🔍 [DATA_CONTEXT] loadMealPlan response:', {
        success: response.success,
        hasData: !!response.data,
        hasMealPlan: !!response.data?.mealPlan,
        mealPlanKeys: response.data?.mealPlan ? Object.keys(response.data.mealPlan) : [],
        hasRawIngredients: !!response.data?.mealPlan?.rawIngredients,
        rawIngredientsLength: response.data?.mealPlan?.rawIngredients?.length || 0
      });
      
      if (response.success && response.data?.mealPlan) {
        // Update the query cache to immediately reflect the loaded meal plan
        // The cache expects the meal plan data directly, not wrapped in an object
        queryClient.setQueryData(mealPlanQueryKey, response.data.mealPlan);
        return response.data.mealPlan;
      }
      return null;
    } catch (error) {
      console.error('Failed to load meal plan:', error);
      return null;
    }
  }, [queryClient, mealPlanQueryKey]);

  const deleteMealPlan = useCallback(async (weekStartDate: string) => {
    try {
      const response = await MealPlannerAPI.deleteMealPlan(weekStartDate);
      if (response.success) {
        toast.success('Meal plan deleted successfully!');
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to delete meal plan');
      return false;
    }
  }, [queryClient, mealPlanQueryKey]);

  const copyMealPlan = useCallback(async (sourceWeek: string, targetWeek: string) => {
    try {
      const response = await MealPlannerAPI.copyMealPlanToWeek(sourceWeek, targetWeek);
      if (response.success) {
        toast.success('Meal plan copied successfully!');
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to copy meal plan');
      return false;
    }
  }, [queryClient, mealPlanQueryKey]);

  const rerollMeal = useCallback(async (request: any) => {
    try {
      await rerollMealMutation.mutateAsync(request);
      return true;
    } catch (error) {
      return false;
    }
  }, [rerollMealMutation]);

  const updateGroceryList = useCallback(async (weekStartDate: string, updates: any) => {
    try {
      const response = await MealPlannerAPI.updateGroceryListState(weekStartDate, updates);
      if (response.success) {
        toast.success('Grocery list updated successfully!');
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to update grocery list');
      return false;
    }
  }, [queryClient, mealPlanQueryKey]);

  const updateSideDishes = useCallback(async (weekStartDate: string, mealId: number, sideDishIds: number[]) => {
    try {
      const response = await MealPlannerAPI.updateSideDishSelections(weekStartDate, mealId, sideDishIds);
      if (response.success) {
        toast.success('Side dishes updated successfully!');
        queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to update side dishes');
      return false;
    }
  }, [queryClient, mealPlanQueryKey]);

  const rerollSideDish = useCallback(async (request: any) => {
    try {
      await rerollSideDishMutation.mutateAsync(request);
      return true;
    } catch (error) {
      return false;
    }
  }, [rerollSideDishMutation]);

  const addSideDish = useCallback(async (request: any) => {
    try {
      const response = await MealPlannerAPI.addSideDish(request);
      if (response.success && response.data) {
        toast.success('Side dish added successfully!');

        const data = response.data as any;
        if (Array.isArray(data?.days)) {
          // Full meal plan
          queryClient.setQueryData(mealPlanQueryKey, data);
        } else if (data?.id) {
          // Single meal
          const updatedMeal = data;
          queryClient.setQueryData(mealPlanQueryKey, (prev: any) => {
            if (!prev || !Array.isArray(prev?.days)) return prev;
            return {
              ...prev,
              days: prev.days.map((day: any) => ({
                ...day,
                meals: day.meals.map((meal: any) =>
                  meal.id === updatedMeal.id
                    ? {
                        ...meal,
                        sideDishes: updatedMeal?.sideDishes ?? meal.sideDishes,
                        ingredients: updatedMeal?.ingredients ?? meal.ingredients,
                      }
                    : meal
                ),
              })),
            };
          });
          queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        } else {
          // Unexpected shape: fetch latest from server
          queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        }

        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to add side dish');
      return false;
    }
  }, [queryClient, mealPlanQueryKey]);

  const removeSideDish = useCallback(async (request: any) => {
    try {
      const response = await MealPlannerAPI.removeSideDish(request);
      if (response.success && response.data) {
        toast.success('Side dish removed successfully!');

        const data = response.data as any;
        if (Array.isArray(data?.days)) {
          // Full meal plan
          queryClient.setQueryData(mealPlanQueryKey, data);
        } else if (data?.id) {
          // Single meal
          const updatedMeal = data;
          queryClient.setQueryData(mealPlanQueryKey, (prev: any) => {
            if (!prev || !Array.isArray(prev?.days)) return prev;
            return {
              ...prev,
              days: prev.days.map((day: any) => ({
                ...day,
                meals: day.meals.map((meal: any) =>
                  meal.id === updatedMeal.id
                    ? {
                        ...meal,
                        sideDishes: updatedMeal?.sideDishes ?? meal.sideDishes,
                        ingredients: updatedMeal?.ingredients ?? meal.ingredients,
                      }
                    : meal
                ),
              })),
            };
          });
          queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        } else {
          // Unexpected shape
          queryClient.invalidateQueries({ queryKey: mealPlanQueryKey });
        }

        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to remove side dish');
      return false;
    }
  }, [queryClient, mealPlanQueryKey]);

  const saveUserPreferences = useCallback(async (preferences: UserPreferences) => {
    try {
      await savePreferencesMutation.mutateAsync(preferences);
      return true;
    } catch (error) {
      return false;
    }
  }, [savePreferencesMutation]);

  const loadUserPreferences = useCallback(async () => {
    try {
      if (!getUserId) return null;
      
      const response = await MealPlannerAPI.getUserPreferences(getUserId);
      return response.success ? response.data || null : null;
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      return null;
    }
  }, [getUserId]);

  const resetUserPreferences = useCallback(async () => {
    try {
      if (!getUserId) return false;
      
      const response = await MealPlannerAPI.getDefaultPreferences(getUserId);
      if (response.success && response.data) {
        await savePreferencesMutation.mutateAsync(response.data);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [savePreferencesMutation, getUserId]);

  const updateAutoSaveBehavior = useCallback(async (behavior: AutoSaveBehavior) => {
    try {
      await updateAutoSaveBehaviorMutation.mutateAsync(behavior);
      return true;
    } catch (error) {
      return false;
    }
  }, [updateAutoSaveBehaviorMutation]);

  const invalidateQueries = useCallback((queryKey: string[]) => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient]);

  const refetchQueries = useCallback((queryKey: string[]) => {
    queryClient.refetchQueries({ queryKey });
  }, [queryClient]);

  // Context value
  const value = useMemo((): DataContextType => ({
    mealPlans: {
      current: currentMealPlan || null,
      isLoading: mealPlanLoading,
      error: mealPlanError?.message || null,
      
      generate: generateMealPlan,
      save: saveMealPlan,
      saveAnonymous: saveAnonymousMealPlan,
      load: loadMealPlan,
      delete: deleteMealPlan,
      copy: copyMealPlan,
      rerollMeal: rerollMeal,
      updateGroceryList: updateGroceryList,
      updateSideDishes: updateSideDishes,
      
      sideDishes: {
        reroll: rerollSideDish,
        add: addSideDish,
        remove: removeSideDish,
      },
    },
    
    preferences: {
      data: userPreferences || null,
      isLoading: preferencesLoading,
      error: preferencesError?.message || null,
      
      save: saveUserPreferences,
      load: loadUserPreferences,
      reset: resetUserPreferences,
    },
    
    autoSave: {
      status: autoSaveStatus || null,
      isLoading: autoSaveLoading,
      
      updateBehavior: updateAutoSaveBehavior,
    },
    
    sideDishes: {
      reroll: rerollSideDish,
      add: addSideDish,
      remove: removeSideDish,
    },
    
    invalidateQueries: invalidateQueries,
    refetchQueries: refetchQueries,
  }), [
    currentMealPlan,
    mealPlanLoading,
    mealPlanError?.message,
    userPreferences,
    preferencesLoading,
    preferencesError?.message,
    autoSaveStatus,
    autoSaveLoading,
    generateMealPlan,
    saveMealPlan,
    saveAnonymousMealPlan,
    loadMealPlan,
    deleteMealPlan,
    copyMealPlan,
    rerollMeal,
    updateGroceryList,
    updateSideDishes,
    rerollSideDish,
    addSideDish,
    removeSideDish,
    saveUserPreferences,
    loadUserPreferences,
    resetUserPreferences,
    updateAutoSaveBehavior,
    invalidateQueries,
    refetchQueries
  ]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

// Hook to use data context
export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

// Export query client for external use if needed
export { queryClient };
